/**
 * Reverificarea unei alegeri contra meniului ADRESEI.
 *
 * Meniul generic (deschis inainte de adresa) si meniul pentru o adresa pot
 * diferi: pretul e al ofertei (ADR-010), dar disponibilitatea, limitele si mai
 * ales OPTIUNILE EXECUTABILE depind de restaurantele care chiar livreaza acolo
 * (ADR-025 — un extra disponibil doar la o bucatarie). De aceea nimic din ce a
 * apasat clientul inainte de adresa nu intra in cos „pe cuvant": se reconstruie
 * din snapshotul nou, iar ce nu se poate reconstrui se marcheaza indisponibil,
 * cu explicatie, nu se sterge in tacere.
 *
 * Modulul e pur: nici React, nici retea. Aceeasi functie deserveste adaugarea
 * din meniu (cu adresa deja cunoscuta) si revalidarea cosului cand adresa apare
 * sau se schimba.
 */

import type { MenuDto, OfferDto } from "@deliveryapp/shared";

import type { CartLine } from "./CartProvider";
import type { OfferAddPayload } from "./OfferCard";
import { offerCustomerUnitPrice, offerPromotion } from "./offer-merchandising";
import {
  cartLineIdFor,
  projectOptionGroupsForSelection,
  readOfferOptions,
  resolveSelection,
  selectionBlockers,
  selectionFitsFulfillmentVariant,
  selectionPriceDelta,
  type OptionSelection,
} from "./offer-options";

/**
 * Reconstruieste alegerea exclusiv din meniul incarcat pentru adresa curenta.
 * DTO-ul generic pe care s-a apasat inainte de adresa nu este niciodata pus
 * direct in cos: pretul, denumirile, limitele si variantele executabile se iau
 * din snapshotul nou. `null` = alegerea nu se poate onora la adresa asta.
 */
export function refreshedAddPayload(offer: OfferDto, requested: OfferAddPayload): OfferAddPayload | null {
  if (!offer.available) return null;
  if (!Number.isSafeInteger(requested.qty) || requested.qty < Math.max(1, offer.minQty)) return null;
  if (offer.maxQty !== null && offer.maxQty !== undefined && requested.qty > offer.maxQty) return null;

  const options = readOfferOptions(offer);
  if (options.unreadable) return null;
  const selection: OptionSelection = {};
  for (const selected of requested.options) {
    const group = options.groups.find((candidate) => candidate.id === selected.groupId);
    const option = group?.options.find((candidate) => candidate.id === selected.optionId);
    if (group === undefined || option === undefined || !option.available) return null;
    const chosen = selection[group.id] ?? [];
    if (chosen.includes(option.id)) return null;
    selection[group.id] = [...chosen, option.id];
  }

  const projected = projectOptionGroupsForSelection(options.groups, options.variants, selection);
  if (selectionBlockers(projected, selection).length > 0) return null;
  if (!selectionFitsFulfillmentVariant(options.variants, selection, true)) return null;
  const resolved = resolveSelection(projected, selection);
  if (resolved.length !== requested.options.length) return null;

  const priceDelta = selectionPriceDelta(projected, selection);
  if (offerCustomerUnitPrice(offer) + priceDelta < 0) return null;
  const promotion = offerPromotion(offer);
  const imageUrl = [...offer.images].sort((a, b) => a.sortOrder - b.sortOrder)[0]?.url ?? offer.imageUrl ?? null;
  return {
    lineId: cartLineIdFor(offer.offerId, resolved),
    unitPriceGross: offerCustomerUnitPrice(offer) + priceDelta,
    compareAtUnitPrice: promotion === null ? null : promotion.compareAtPrice + priceDelta,
    options: resolved,
    imageUrl,
    qty: requested.qty,
    // Mentiunile sunt ale clientului, nu ale meniului: trec neschimbate.
    notes: requested.notes ?? null,
  };
}

/** Linia de cos, ca cerere de adaugare: exact ce a vrut clientul, fara preturi. */
export function requestFromLine(line: CartLine): OfferAddPayload {
  return {
    lineId: line.lineId,
    unitPriceGross: line.unitPriceGross,
    compareAtUnitPrice: line.compareAtUnitPrice ?? null,
    options: line.selectedOptions.map((option, index) => {
      const label = line.optionLabels[index] ?? "";
      const separator = label.indexOf(": ");
      return {
        groupId: option.groupId,
        groupName: separator > 0 ? label.slice(0, separator) : "",
        optionId: option.optionId,
        optionName: separator > 0 ? label.slice(separator + 2) : label,
        priceDelta: option.priceDelta ?? 0,
      };
    }),
    imageUrl: line.imageUrl,
    qty: line.qty,
    notes: line.notes,
  };
}

export interface RevalidationResult {
  lines: CartLine[];
  /** Numele produselor care nu se pot pregati la adresa; raman in cos, marcate. */
  unavailable: string[];
  /** Cate linii si-au schimbat pretul sau eticheta fata de ce era in cos. */
  changed: number;
}

/**
 * Trece fiecare linie prin meniul adresei.
 *
 * Liniile care nu se pot onora NU se sterg: raman in cos cu
 * `unavailableAtAddress`, ca sa poata fi vazute si scoase de client — o linie
 * disparuta in tacere e o comanda care ajunge fara desertul copilului si nimeni
 * nu stie de ce. Finalizarea ramane blocata cat timp exista una.
 */
export function revalidateCartLines(lines: readonly CartLine[], menus: readonly MenuDto[], addressKey: string): RevalidationResult {
  const offersById = new Map<string, OfferDto>();
  for (const menu of menus) for (const offer of menu.offers) offersById.set(offer.offerId, offer);

  const unavailable: string[] = [];
  let changed = 0;
  const next = lines.map((line) => {
    const offer = offersById.get(line.offerId);
    const refreshed = offer === undefined ? null : refreshedAddPayload(offer, requestFromLine(line));
    if (offer === undefined || refreshed === null) {
      unavailable.push(line.name);
      return { ...line, unavailableAtAddress: true, verifiedForAddressKey: addressKey };
    }
    const optionLabels = refreshed.options.map((option) => `${option.groupName}: ${option.optionName}`);
    if (
      refreshed.unitPriceGross !== line.unitPriceGross ||
      (refreshed.compareAtUnitPrice ?? null) !== (line.compareAtUnitPrice ?? null) ||
      optionLabels.join("|") !== line.optionLabels.join("|")
    ) {
      changed += 1;
    }
    return {
      ...line,
      name: offer.name,
      unitPriceGross: refreshed.unitPriceGross,
      compareAtUnitPrice: refreshed.compareAtUnitPrice,
      imageUrl: refreshed.imageUrl ?? line.imageUrl,
      optionLabels,
      selectedOptions: refreshed.options.map((option) => ({
        groupId: option.groupId,
        optionId: option.optionId,
        priceDelta: option.priceDelta,
      })),
      maxQty: offer.maxQty ?? null,
      minQty: offer.minQty,
      unavailableAtAddress: false,
      verifiedForAddressKey: addressKey,
    };
  });
  return { lines: next, unavailable, changed };
}

export function addressKeyOf(address: { lat: number; lng: number } | null): string | null {
  return address === null ? null : `${address.lat}:${address.lng}`;
}
